// src/services/inAppNotifications.js
// Firestore helpers for in-app notification announcements
// Collection: inAppNotifications — admin-created announcements
// Collection: notificationDismissals — tracks which users have seen which notifications
// Doc ID pattern: ${userId}_${notificationId}

import { db, firebaseEnabled } from './firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc, deleteDoc, updateDoc, addDoc } from './firestoreWrite';

// Optional image (2026-10-06): same contract as Coach Posts — imageUrl ('' =
// none) + imageSize small|medium|large (anything else ⇒ medium). Absent on
// every announcement saved before this ⇒ no image, popup unchanged.
const IMAGE_SIZES = ['small', 'medium', 'large'];
const cleanImageSize = (s) => (IMAGE_SIZES.includes(s) ? s : 'medium');

// Audience (2026-10-07): 'all' (default) or 'members' (active monthly
// subscription; admin/friend included — same `isMember` gate as Coach Posts).
// Only a literal 'members' narrows it, so every announcement saved before this
// field existed stays visible to everyone, exactly as before. Presentation
// gate, not access control — the doc itself remains readable.
export const cleanAudience = (a) => (a === 'members' ? 'members' : 'all');
export const isForMembersOnly = (n) => cleanAudience(n && n.audience) === 'members';

// ═══════════════════════════════════════════════════════════════
//  ADMIN: CRUD operations for in-app notifications
// ═══════════════════════════════════════════════════════════════

export async function createNotification({ title, body, ctaLabel, ctaLink, durationDays, createdBy, imageUrl, imageSize, audience }) {
    if (!firebaseEnabled || !db) return null;
    try {
        const docRef = await addDoc(collection(db, 'inAppNotifications'), {
            title: title.trim(),
            body: body.trim(),
            ctaLabel: (ctaLabel || '').trim() || 'Got it!',
            ctaLink: (ctaLink || '').trim(),
            durationDays: Number(durationDays) || 7,
            imageUrl: (imageUrl || '').trim(),
            imageSize: cleanImageSize(imageSize),
            audience: cleanAudience(audience),
            active: true,
            createdAt: new Date().toISOString(),
            createdBy: createdBy || '',
        });
        return docRef.id;
    } catch (e) {
        console.error('Error creating in-app notification:', e);
        return null;
    }
}

export async function updateNotification(notificationId, updates) {
    if (!firebaseEnabled || !db || !notificationId) return false;
    try {
        const ref = doc(db, 'inAppNotifications', notificationId);
        // Only allow safe fields to be updated
        const safeFields = {};
        if (updates.title !== undefined) safeFields.title = updates.title.trim();
        if (updates.body !== undefined) safeFields.body = updates.body.trim();
        if (updates.ctaLabel !== undefined) safeFields.ctaLabel = (updates.ctaLabel || '').trim() || 'Got it!';
        if (updates.ctaLink !== undefined) safeFields.ctaLink = (updates.ctaLink || '').trim();
        if (updates.durationDays !== undefined) safeFields.durationDays = Number(updates.durationDays) || 7;
        if (updates.active !== undefined) safeFields.active = !!updates.active;
        // Image fields MUST be listed here or an edit silently drops the image.
        if (updates.imageUrl !== undefined) safeFields.imageUrl = (updates.imageUrl || '').trim();
        if (updates.imageSize !== undefined) safeFields.imageSize = cleanImageSize(updates.imageSize);
        if (updates.audience !== undefined) safeFields.audience = cleanAudience(updates.audience);
        safeFields.updatedAt = new Date().toISOString();
        await updateDoc(ref, safeFields);
        return true;
    } catch (e) {
        console.error('Error updating in-app notification:', e);
        return false;
    }
}

export async function deleteNotification(notificationId) {
    if (!firebaseEnabled || !db || !notificationId) return false;
    try {
        await deleteDoc(doc(db, 'inAppNotifications', notificationId));
        return true;
    } catch (e) {
        console.error('Error deleting in-app notification:', e);
        return false;
    }
}

export async function getAllNotifications() {
    if (!firebaseEnabled || !db) return [];
    try {
        const q = query(collection(db, 'inAppNotifications'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
        console.error('Error fetching in-app notifications:', e);
        return [];
    }
}

// ═══════════════════════════════════════════════════════════════
//  USER: Get the most recent unseen, active, non-expired notification
// ═══════════════════════════════════════════════════════════════

// D-073 — COST OPTIMIZATION: sessionStorage cache for getUnseenNotification.
// Previously this ran on every Dashboard mount: 1× collection scan of active
// inAppNotifications + 1× per-user scan of notificationDismissals. With 1500
// users hitting Dashboard ~multiple times/day this was ≥2 reads/mount/user and
// a major contributor to daily Firestore read count.
//
// Contract preserved: still returns the most recent unseen, active,
// non-expired notification OR null. Shape of the returned object unchanged.
//
// Staleness tolerance:
//   • New admin notifications may take up to CACHE_TTL_MS (15 min) to appear
//     for users mid-session. Acceptable — notifications are non-urgent nudges.
//   • Per-tab (sessionStorage). A second tab/device won't share the cache,
//     matching existing behavior where cross-tab dismissals are already not
//     reflected without a reload.
//   • `dismissNotification` invalidates the cache synchronously so the very
//     next call reflects the dismissal. Any dismissal done elsewhere (another
//     device) stays stale until TTL, same as the pre-cache behavior once the
//     page had mounted.
// Safe-fail: any sessionStorage/JSON parse error falls through to a live fetch.
const CACHE_TTL_MS = 15 * 60 * 1000;
// Keyed by audience too: if a member's subscription starts or ends mid-session
// she must not be served the answer computed for the other audience.
const cacheKey = (userId, isMember) => `mom_unseen_notif_${userId}${isMember ? '_m' : ''}`;

function readCachedUnseen(userId, isMember) {
    try {
        if (typeof sessionStorage === 'undefined') return undefined;
        const raw = sessionStorage.getItem(cacheKey(userId, isMember));
        if (!raw) return undefined;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.cachedAt !== 'number') return undefined;
        if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return undefined;
        // `result` may legitimately be null (no unseen notification) — that's
        // a cacheable answer. Distinguish by checking property existence.
        if (!('result' in parsed)) return undefined;
        return parsed.result;
    } catch (e) {
        return undefined;
    }
}

function writeCachedUnseen(userId, isMember, result) {
    try {
        if (typeof sessionStorage === 'undefined') return;
        sessionStorage.setItem(cacheKey(userId, isMember), JSON.stringify({
            cachedAt: Date.now(),
            result,
        }));
    } catch (e) {
        // quota / disabled storage — silent, we just skip caching
    }
}

function invalidateCachedUnseen(userId) {
    try {
        if (typeof sessionStorage === 'undefined') return;
        sessionStorage.removeItem(cacheKey(userId, false));
        sessionStorage.removeItem(cacheKey(userId, true));
    } catch (e) { /* silent */ }
}

// `isMember` (optional, default false): when false, members-only announcements
// are skipped DURING selection — never filtered after — so a members-only post
// can't shadow an older all-users one for a non-member.
export async function getUnseenNotification(userId, { isMember = false } = {}) {
    if (!firebaseEnabled || !db || !userId) return null;

    // Return cached result if still fresh. `undefined` = cache miss/expired;
    // `null` = legitimately cached "no notification"; otherwise an object.
    const cached = readCachedUnseen(userId, isMember);
    if (cached !== undefined) {
        // Re-verify expiration client-side — a notification cached as active
        // might have crossed its durationDays boundary since we cached it.
        if (cached && (isExpired(cached) || (!isMember && isForMembersOnly(cached)))) {
            invalidateCachedUnseen(userId);
        } else {
            return cached;
        }
    }

    try {
        // Fetch all active notifications
        const q = query(
            collection(db, 'inAppNotifications'),
            where('active', '==', true),
            orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        if (snap.empty) {
            writeCachedUnseen(userId, isMember, null);
            return null;
        }

        const now = new Date();
        // Filter to non-expired
        const active = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((n) => {
                const created = new Date(n.createdAt);
                const expiresAt = new Date(created.getTime() + (n.durationDays || 7) * 86400000);
                return expiresAt > now;
            })
            .filter((n) => isMember || !isForMembersOnly(n));

        if (active.length === 0) {
            writeCachedUnseen(userId, isMember, null);
            return null;
        }

        // Fetch user's dismissals to find which ones they've already seen
        const dismissQ = query(
            collection(db, 'notificationDismissals'),
            where('userId', '==', userId)
        );
        const dismissSnap = await getDocs(dismissQ);
        const dismissedIds = new Set(dismissSnap.docs.map((d) => d.data().notificationId));

        // Return the most recent unseen notification
        const result = active.find((n) => !dismissedIds.has(n.id)) || null;
        writeCachedUnseen(userId, isMember, result);
        return result;
    } catch (e) {
        console.error('Error fetching unseen notification:', e);
        return null;
    }
}

// ═══════════════════════════════════════════════════════════════
//  USER: Dismiss a notification (permanent — any dismiss method)
// ═══════════════════════════════════════════════════════════════

export async function dismissNotification(userId, notificationId) {
    if (!firebaseEnabled || !db || !userId || !notificationId) return false;
    try {
        const dismissId = `${userId}_${notificationId}`;
        await setDoc(doc(db, 'notificationDismissals', dismissId), {
            userId,
            notificationId,
            dismissedAt: new Date().toISOString(),
        });
        // D-073 — invalidate cache so the NEXT getUnseenNotification call reflects
        // the dismissal and returns the next-in-line unseen (or null) instead of
        // re-serving the one we just dismissed.
        invalidateCachedUnseen(userId);
        return true;
    } catch (e) {
        console.error('Error dismissing notification:', e);
        return false;
    }
}

// ═══════════════════════════════════════════════════════════════
//  HELPER: Check if a notification is expired
// ═══════════════════════════════════════════════════════════════

export function isExpired(notification) {
    if (!notification || !notification.createdAt) return true;
    const created = new Date(notification.createdAt);
    const expiresAt = new Date(created.getTime() + (notification.durationDays || 7) * 86400000);
    return expiresAt < new Date();
}

export function daysRemaining(notification) {
    if (!notification || !notification.createdAt) return 0;
    const created = new Date(notification.createdAt);
    const expiresAt = new Date(created.getTime() + (notification.durationDays || 7) * 86400000);
    const remaining = Math.ceil((expiresAt - new Date()) / 86400000);
    return Math.max(0, remaining);
}
